import { ChangeDetectionStrategy, Component, Inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, finalize, of } from 'rxjs';
import { USER_TYPE } from 'src/app/core/helpers/global/global.constant';
import { PLAYER_REGISTER_VALIDATIONS } from 'src/app/core/helpers/global/player.constant';
import { TYPES_USER_PERMISSIONS } from 'src/app/core/helpers/global/user-management.constant';
import { NG_SELECT_QUERIES } from 'src/app/core/helpers/ui/ui.constant';
import { Player, QuickRegisterPlayerPayload } from 'src/app/core/interfaces/api/player.interface';
import { ModalWithAction } from 'src/app/core/interfaces/ui/bootstrap-modal.interface';
import { NgSelect, NgSelectQuery } from 'src/app/core/interfaces/ui/ui.interface';
import { PlayerService } from 'src/app/core/services/api/player.service';
import { UserService } from 'src/app/core/services/api/user.service';
import { BootstrapModalService } from 'src/app/core/services/ui/bootstrap-modal.service';
import { FilterService } from 'src/app/core/services/ui/filter.service';
import { SearchNgSelectService } from 'src/app/core/services/ui/search-ng-select.service';
import { AppState } from 'src/app/core/states/app.state';
import { selectUser } from 'src/app/core/states/auth/auth.selectors';
import { phoneNumberValidator } from 'src/app/shared/validators/validatorsPhoneNumber';

@Component({
  selector: 'quick-player-modal-form',
  templateUrl: './quick-player-modal-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: 'ngUsers', useClass: SearchNgSelectService }],
})
export class QuickPlayerModalFormComponent implements OnInit {
  public NG_SELECT_QUERIES = NG_SELECT_QUERIES;
  public PLAYER_REGISTER_VALIDATIONS = PLAYER_REGISTER_VALIDATIONS;

  public users$: Observable<NgSelect<string>[]> = of([]);

  public quickPlayerForm: FormGroup | undefined = undefined;

  public showPassword = signal(false);
  public showConfirmPassword = signal(false);
  public isSubmitting = signal(false);

  public readonly profile = toSignal(this._store.select(selectUser));

  constructor(
    @Inject('ngUsers')
    private readonly _ngUsers: SearchNgSelectService<string>,
    private readonly _userService: UserService,
    private readonly _bsModalService: BootstrapModalService<ModalWithAction<Player>>,
    private readonly _filterService: FilterService<object>,
    private readonly _formBuilder: FormBuilder,
    private readonly _playerService: PlayerService,
    private readonly _store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.setConfigNgSelect();
    this.buildForm();
  }

  get isCollaboratorAgent(): boolean {
    return this.profile()?.userType === TYPES_USER_PERMISSIONS.COLLABORATOR_AGENT;
  }

  get isAgentOrCashdesk(): boolean {
    return (
      this.profile()?.userType === TYPES_USER_PERMISSIONS.AGENT ||
      this.profile()?.userType === TYPES_USER_PERMISSIONS.CASHDESK
    );
  }

  get canSelectAgent(): boolean {
    return !this.isAgentOrCashdesk && !this.isCollaboratorAgent;
  }

  private setConfigNgSelect(): void {
    this._ngUsers.setSearchTermKey('username');
    this._ngUsers.extendFilter({ type: USER_TYPE.AGENT });
    this._ngUsers.setFetchDataFunction(
      this._userService.findUsersForSelect.bind(this._userService)
    );
    this.users$ = this._ngUsers.getData();
    this._ngUsers.triggerFetchData();
  }

  private buildForm(): void {
    this.quickPlayerForm = this._formBuilder.group(
      {
        username: ['', Validators.required],
        email: [null, [Validators.required, Validators.email]],
        mobileNumber: [null, [Validators.required, phoneNumberValidator()]],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(PLAYER_REGISTER_VALIDATIONS.PASSWORD.MIN_LENGTH),
            Validators.maxLength(PLAYER_REGISTER_VALIDATIONS.PASSWORD.MAX_LENGTH),
          ],
        ],
        confirmPassword: ['', Validators.required],
        ...(this.canSelectAgent && { agentId: [null, Validators.required] }),
      },
      { validators: this.passwordMatchValidator }
    );
  }

  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    return group.get('password')?.value === group.get('confirmPassword')?.value
      ? null
      : { mismatch: true };
  }

  public onSearchSelect(typeQuery: NgSelectQuery, searchTerm?: string): void {
    if (typeQuery === NG_SELECT_QUERIES.USERS) this._ngUsers.searchTerm(searchTerm || '');
  }

  public onScrollToEndSelect(typeQuery: NgSelectQuery): void {
    if (typeQuery === NG_SELECT_QUERIES.USERS) this._ngUsers.scrollToEnd();
  }

  public onSubmit(): void {
    if (!this.quickPlayerForm || this.isSubmitting()) return;

    if (this.quickPlayerForm.invalid) {
      this.quickPlayerForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    this._playerService
      .quickRegisterPlayer(this.buildPayload())
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: () => {
          this._filterService.updateFilterData({});
          this.closeModal();
        },
      });
  }

  public togglePasswordVisibility(): void {
    this.showPassword.update((value) => !value);
  }

  public toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword.update((value) => !value);
  }

  public closeModal(): void {
    this._bsModalService.closeModal();
  }

  private buildPayload(): QuickRegisterPlayerPayload {
    const { username, email, mobileNumber, password, agentId } =
      this.quickPlayerForm!.value;

    return {
      username,
      email,
      mobileNumber,
      password,
      ...(this.canSelectAgent && agentId && { agentId }),
    };
  }
}
